import { createCheckoutSession } from "./api-functions";
import { PaymentRequiredResponse, PricingPlan } from "../types";
import { AttributionCapture } from "@/app/lib/tracking/attribution";
import { createLogger } from "./logger";

const log = createLogger("PublishUtils");

/**
 * Creates a checkout session and redirects to Stripe
 * @param paymentResponse - The payment required response from the backend
 * @param successUrl - URL to redirect after successful payment
 * @param cancelUrl - URL to redirect after cancelled payment
 * @param priceName - The price name for the subscription
 */
export async function handlePaymentRedirect(
  paymentResponse: PaymentRequiredResponse,
  successUrl: string,
  cancelUrl: string,
  priceName: PricingPlan = "starter",
  currency: string = "CHF"
): Promise<void> {
  try {
    // Capture attribution before redirecting to Stripe
    const attribution = await AttributionCapture.capture();
    const checkoutSession = await createCheckoutSession({
      price_name: priceName,
      currency: currency,
      success_url: successUrl,
      cancel_url: cancelUrl,
      page_id: paymentResponse.page_id,
      attribution_data: {
        utm_source: attribution.utmSource || "",
        utm_medium: attribution.utmMedium || "",
        utm_campaign: attribution.utmCampaign || "",
        utm_content: attribution.utmContent || "",
        referrer: attribution.referrer || "",
      },
    });

    // Redirect to Stripe checkout
    if (typeof window !== "undefined") {
      window.location.href = checkoutSession.url;
    }
  } catch (error) {
    log.error("Failed to create checkout session", error);
    throw new Error("Failed to initiate payment. Please try again.");
  }
}

/**
 * Default payment required handler that redirects to Stripe
 * @param paymentResponse - The payment required response
 * @param currentUrl - The current URL to use for success/cancel redirects
 * @param selectedPlan - The selected pricing plan
 */
export function defaultPaymentHandler(
  paymentResponse: PaymentRequiredResponse,
  currentUrl: string,
  selectedPlan: PricingPlan = "starter",
  currency: string = "CHF"
): void {
  let successUrl = `${currentUrl}?checkoutSucceeded=true`;
  let cancelUrl = `${currentUrl}?canceled=true`;
  const publishedId = paymentResponse.page_id;
  // Persist the page id we are publishing so the dashboard can poll it after checkout
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem("publishingPageId", String(publishedId));
    } catch {}
  }
  try {
    const base = new URL(currentUrl);
    // Success URL with checkoutSucceeded flag
    base.searchParams.set("checkoutSucceeded", "true");
    // include the explicit page id for post-checkout polling
    base.searchParams.set("publishedId", String(publishedId));
    successUrl = base.toString();
    // Cancel URL with canceled flag
    base.searchParams.delete("checkoutSucceeded");
    base.searchParams.delete("publishedId");
    base.searchParams.set("canceled", "true");
    cancelUrl = base.toString();
  } catch {}

  handlePaymentRedirect(
    paymentResponse,
    successUrl,
    cancelUrl,
    selectedPlan,
    currency
  ).catch(error => {
    log.error("Payment redirect failed", error, { pageId: paymentResponse.page_id });
  });
}
